import * as v from 'valibot';
import { PiiClass, PolicySpec, RedactionMode, parsePolicy } from '@reef/schema';

/**
 * The edit vocabulary behind "chat-to-edit" policy building.
 *
 * The natural-language path does not let a model hand us a whole new policy
 * document — that would make "only allow 10 rows from users" capable of
 * silently dropping an unrelated deny pattern. Instead the model emits one or
 * more of these narrow operations, we apply them to the current policy, and the
 * result is re-validated. The visual builder emits exactly the same operations,
 * so both editors share one code path and one audit shape.
 *
 * Every rule in `PolicySpec` is reachable from here, and that is the property
 * worth keeping. The list was once additive-only — `allow_tables` could add a
 * table to the allowlist and nothing could take one off — which made the
 * builder a ratchet: the only way to loosen a policy by a single table was to
 * hand-edit the JSON in the config repo, and the screen that claimed to be the
 * policy editor was a policy *tightener*. So each additive operation has a
 * removal beside it, and the two clears below exist because emptying a list is
 * not the same act as removing the last thing from it.
 */
export const PolicyOperation = v.variant('op', [
  v.object({ op: v.literal('set_description'), description: v.string() }),
  v.object({ op: v.literal('set_max_rows'), maxRows: v.pipe(v.number(), v.integer(), v.minValue(0)) }),
  v.object({ op: v.literal('set_max_bytes'), maxBytes: v.pipe(v.number(), v.integer(), v.minValue(256)) }),
  v.object({ op: v.literal('set_read_only'), readOnly: v.boolean() }),
  v.object({
    op: v.literal('set_table_max_rows'),
    table: v.string(),
    maxRows: v.pipe(v.number(), v.integer(), v.minValue(0)),
  }),
  v.object({ op: v.literal('remove_table_rule'), table: v.string() }),
  v.object({ op: v.literal('allow_tables'), tables: v.array(v.string()) }),
  v.object({ op: v.literal('remove_tables'), tables: v.array(v.string()) }),
  v.object({ op: v.literal('clear_table_allowlist') }),
  v.object({ op: v.literal('allow_columns'), columns: v.array(v.string()), table: v.optional(v.string()) }),
  v.object({
    op: v.literal('remove_allowed_columns'),
    columns: v.array(v.string()),
    table: v.optional(v.string()),
  }),
  v.object({ op: v.literal('clear_allowed_columns'), table: v.optional(v.string()) }),
  v.object({ op: v.literal('deny_columns'), columns: v.array(v.string()), table: v.optional(v.string()) }),
  v.object({
    op: v.literal('remove_denied_columns'),
    columns: v.array(v.string()),
    table: v.optional(v.string()),
  }),
  v.object({
    op: v.literal('add_deny_pattern'),
    id: v.string(),
    pattern: v.string(),
    action: v.optional(v.picklist(['block', 'redact']), 'block'),
    /** Which side of the call to scan. Defaults to both, which is the safe reading. */
    appliesTo: v.optional(v.picklist(['input', 'output', 'both']), 'both'),
    /** What a `redact` match is replaced with. Ignored when the action is `block`. */
    replacement: v.optional(v.string(), '[redacted]'),
    reason: v.optional(v.string()),
  }),
  v.object({ op: v.literal('remove_deny_pattern'), id: v.string() }),
  v.object({
    op: v.literal('redact_pii'),
    classes: v.array(PiiClass),
    mode: v.optional(RedactionMode),
  }),
  v.object({ op: v.literal('remove_pii'), classes: v.array(PiiClass) }),
  v.object({ op: v.literal('set_redaction_mode'), mode: RedactionMode }),
]);
export type PolicyOperation = v.InferOutput<typeof PolicyOperation>;

export interface PolicyPatchResult {
  policy: PolicySpec;
  /** Human-readable changelog, shown in the UI diff and written to the audit log. */
  changes: string[];
}

/**
 * A removal that would leave a policy in a state nobody asked for.
 *
 * Its own class because these are decisions, not faults: `errors.ts` renders it
 * as a 422 naming the operation, the same way a malformed one is refused.
 */
export class PolicyOperationRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PolicyOperationRefused';
  }
}

export function applyPolicyOperations(
  base: PolicySpec,
  operations: readonly PolicyOperation[],
): PolicyPatchResult {
  const draft = structuredClone(base);
  const changes: string[] = [];

  for (const operation of operations) {
    switch (operation.op) {
      /*
       * Every scalar setter is a no-op when the value is already what it is
       * asked to be, and that is load-bearing rather than tidy: the visual
       * editor commits a number field on blur, so tabbing through the limits
       * used to queue three edits nobody made — and the route commits whatever
       * the changelog is not empty for.
       */
      case 'set_description':
        if (draft.description === operation.description) break;
        changes.push(`description "${operation.description}"`);
        draft.description = operation.description;
        break;

      case 'set_max_rows':
        if (draft.maxRows === operation.maxRows) break;
        changes.push(`max rows ${draft.maxRows} → ${operation.maxRows}`);
        draft.maxRows = operation.maxRows;
        break;

      case 'set_max_bytes':
        if (draft.maxBytes === operation.maxBytes) break;
        changes.push(`max bytes ${draft.maxBytes} → ${operation.maxBytes}`);
        draft.maxBytes = operation.maxBytes;
        break;

      case 'set_read_only':
        if (draft.readOnly === operation.readOnly) break;
        changes.push(operation.readOnly ? 'marked read-only' : 'cleared read-only');
        draft.readOnly = operation.readOnly;
        break;

      case 'set_table_max_rows': {
        const existing = findTable(draft, operation.table);
        if (existing?.maxRows === operation.maxRows) break;
        const rule = upsertTable(draft, operation.table);
        changes.push(`${operation.table}: max rows ${rule.maxRows ?? draft.maxRows} → ${operation.maxRows}`);
        rule.maxRows = operation.maxRows;
        break;
      }

      case 'remove_table_rule': {
        const before = draft.tables.length;
        const removing = operation.table.toLowerCase();
        draft.tables = draft.tables.filter((entry) => entry.table.toLowerCase() !== removing);
        if (draft.tables.length === before) break;
        changes.push(`removed the per-table rule for ${operation.table}`);
        break;
      }

      /*
       * Deduplicated case-insensitively, for the same reason `findTable` is:
       * the engine lowercases the allowlist before checking it, so `Users`
       * beside `users` is one grant written twice — a document that reads as
       * two and would survive removing only one of them.
       */
      case 'allow_tables': {
        // Adding nothing changes nothing. Without this an `allow_tables` with
        // an empty list *created* an empty allowlist, which blocks every query
        // the policy covers — the very state `remove_tables` refuses to reach
        // by subtraction, arrived at by addition instead.
        if (operation.tables.length === 0) break;
        const current = draft.tableAllowlist ?? [];
        const held = new Set(current.map((table) => table.toLowerCase()));
        const added = operation.tables.filter((table) => !held.has(table.toLowerCase()));
        if (draft.tableAllowlist && added.length === 0) break;
        const next = [...current, ...added];
        draft.tableAllowlist = next;
        changes.push(`table allowlist: ${next.join(', ')}`);
        break;
      }

      /*
       * Taking the last table off an allowlist is refused rather than obeyed,
       * and the two readings of the empty list are why. `tableAllowlist: []`
       * blocks every query the policy covers; dropping the key entirely allows
       * every table. Both are defensible policies and neither is what "remove
       * `users`" asked for, so the caller has to say which — `clear_table_allowlist`
       * for the wide one, and a policy that really means "nothing" is written
       * as a deny pattern rather than arrived at by subtraction.
       */
      case 'remove_tables': {
        const current = draft.tableAllowlist;
        if (!current) break;
        const removing = new Set(operation.tables.map((table) => table.toLowerCase()));
        const kept = current.filter((table) => !removing.has(table.toLowerCase()));
        if (kept.length === current.length) break;
        if (kept.length === 0) {
          throw new PolicyOperationRefused(
            `removing ${operation.tables.join(', ')} would empty the table allowlist, which blocks every query rather than widening it — use clear_table_allowlist to allow all tables`,
          );
        }
        draft.tableAllowlist = kept;
        changes.push(`table allowlist: ${kept.join(', ')}`);
        break;
      }

      case 'clear_table_allowlist':
        if (!draft.tableAllowlist) break;
        delete draft.tableAllowlist;
        changes.push('cleared the table allowlist — every table is readable again');
        break;

      case 'allow_columns': {
        // As above, and worse: an empty column allowlist projects every row
        // onto nothing, so a step gets `{}` back from every call it makes.
        if (operation.columns.length === 0) break;
        const target = operation.table ? upsertTable(draft, operation.table) : draft;
        const next = [...new Set([...(target.allowedColumns ?? []), ...operation.columns])];
        if (target.allowedColumns && next.length === target.allowedColumns.length) break;
        target.allowedColumns = next;
        changes.push(`${operation.table ? `${operation.table}: ` : ''}allow columns ${next.join(', ')}`);
        break;
      }

      /* Same refusal as `remove_tables`, for the same reason: an empty column
       * allowlist projects every row onto nothing. */
      case 'remove_allowed_columns': {
        const target = operation.table ? findTable(draft, operation.table) : draft;
        const current = target?.allowedColumns;
        if (!current) break;
        const kept = current.filter((column) => !operation.columns.includes(column));
        if (kept.length === current.length) break;
        if (kept.length === 0) {
          throw new PolicyOperationRefused(
            `removing ${operation.columns.join(', ')} would empty the column allowlist, which strips every column from every row — use clear_allowed_columns to return to all columns`,
          );
        }
        target.allowedColumns = kept;
        changes.push(`${operation.table ? `${operation.table}: ` : ''}allow columns ${kept.join(', ')}`);
        break;
      }

      case 'clear_allowed_columns': {
        const target = operation.table ? findTable(draft, operation.table) : draft;
        if (!target?.allowedColumns) break;
        delete target.allowedColumns;
        changes.push(
          `${operation.table ? `${operation.table}: ` : ''}cleared the column allowlist — every column is returned again`,
        );
        break;
      }

      case 'deny_columns': {
        // Symmetry rather than safety here — an empty deny list denies nothing,
        // so creating one is harmless. It is still not what "deny these
        // columns" with no columns asked for, and an operation that writes a
        // key nobody wanted is one more thing in the diff to explain.
        if (operation.columns.length === 0) break;
        const target = operation.table ? upsertTable(draft, operation.table) : draft;
        const next = [...new Set([...(target.deniedColumns ?? []), ...operation.columns])];
        if (target.deniedColumns && next.length === target.deniedColumns.length) break;
        target.deniedColumns = next;
        changes.push(`${operation.table ? `${operation.table}: ` : ''}deny columns ${next.join(', ')}`);
        break;
      }

      /*
       * No refusal here, and the asymmetry is the point: an empty *deny* list
       * denies nothing, which is exactly what removing the last entry means.
       */
      case 'remove_denied_columns': {
        const target = operation.table ? findTable(draft, operation.table) : draft;
        const current = target?.deniedColumns;
        if (!current) break;
        const kept = current.filter((column) => !operation.columns.includes(column));
        if (kept.length === current.length) break;
        const where = operation.table ? `${operation.table}: ` : '';
        if (kept.length === 0) {
          delete target.deniedColumns;
          changes.push(`${where}no denied columns`);
          break;
        }
        target.deniedColumns = kept;
        changes.push(`${where}deny columns ${kept.join(', ')}`);
        break;
      }

      case 'add_deny_pattern': {
        assertValidRegex(operation.pattern);
        draft.denyPatterns = [
          ...draft.denyPatterns.filter((entry) => entry.id !== operation.id),
          {
            id: operation.id,
            pattern: operation.pattern,
            flags: 'i',
            appliesTo: operation.appliesTo,
            action: operation.action,
            replacement: operation.replacement,
            reason: operation.reason,
          },
        ];
        changes.push(`deny pattern "${operation.id}" (${operation.action})`);
        break;
      }

      case 'remove_deny_pattern': {
        const before = draft.denyPatterns.length;
        draft.denyPatterns = draft.denyPatterns.filter((entry) => entry.id !== operation.id);
        if (draft.denyPatterns.length === before) break;
        changes.push(`removed deny pattern "${operation.id}"`);
        break;
      }

      case 'redact_pii': {
        const added = operation.classes.filter((klass) => !draft.redactPii.includes(klass));
        const mode = operation.mode && operation.mode !== draft.redactionMode ? operation.mode : null;
        if (added.length === 0 && !mode) break;
        draft.redactPii = [...draft.redactPii, ...added];
        if (mode) draft.redactionMode = mode;
        changes.push(`redact ${operation.classes.join(', ')} (${draft.redactionMode})`);
        break;
      }

      case 'remove_pii': {
        const kept = draft.redactPii.filter((klass) => !operation.classes.includes(klass));
        if (kept.length === draft.redactPii.length) break;
        draft.redactPii = kept;
        changes.push(`stopped redacting ${operation.classes.join(', ')}`);
        break;
      }

      case 'set_redaction_mode':
        if (draft.redactionMode === operation.mode) break;
        changes.push(`redaction mode ${draft.redactionMode} → ${operation.mode}`);
        draft.redactionMode = operation.mode;
        break;
    }
  }

  /*
   * The version moves only when something did. A save that changed nothing used
   * to still bump it and write a commit, so `git log policies/x.json` filled
   * with revisions whose diff was one integer — and "which version is running"
   * stopped being answerable from the number alone.
   */
  if (changes.length > 0) draft.version = base.version + 1;
  return { policy: parsePolicy(draft), changes };
}

/**
 * A table rule, matched the way the engine matches one.
 *
 * `tablesInSql` lowercases every name it extracts and `tableHintFor` compares
 * against that, so `PolicyEngine` resolves a rule case-insensitively — and it
 * takes the *first* rule that matches. Looking one up by exact case here
 * therefore did not fail cleanly: editing `Users` beside an existing `users`
 * rule appended a second entry, the editor drew both, and the engine went on
 * enforcing the old one. A cap somebody set and could see on screen was not the
 * cap the run got.
 */
function findTable(policy: PolicySpec, table: string) {
  const wanted = table.toLowerCase();
  return policy.tables.find((entry) => entry.table.toLowerCase() === wanted);
}

function upsertTable(policy: PolicySpec, table: string) {
  let rule = findTable(policy, table);
  if (!rule) {
    rule = { table };
    policy.tables.push(rule);
  }
  return rule;
}

/**
 * A deny pattern that never compiles is worse than no pattern: it reads as
 * protection in the UI and silently matches nothing at runtime. Reject at
 * authoring time.
 */
function assertValidRegex(source: string): void {
  try {
    new RegExp(source);
  } catch (error) {
    throw new Error(
      `deny pattern is not a valid regular expression: ${(error as Error).message}`,
    );
  }
}
